import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { DEFAULT_MODEL as DEFAULT_MODEL_METADATA, type Model } from '../components/chat/ModelSelector';

export type { Model };

interface WorkModeState {
  focusMode: boolean;
  selectedModel: Model;
  selectedItemId: string | null;
  view: 'chat' | 'timeline';
  chatScrollTop: number;
  timelineScrollTop: number;
  resolvedCount: number;
  autoAdvance: boolean;
  sidebarOpen: boolean;
}

type WorkModeAction =
  | { type: 'TOGGLE_FOCUS_MODE' }
  | { type: 'SET_FOCUS_MODE'; value: boolean }
  | { type: 'SET_MODEL'; model: Model }
  | { type: 'SET_SELECTED_ITEM'; itemId: string | null }
  | { type: 'SET_VIEW'; view: 'chat' | 'timeline' }
  | { type: 'SET_CHAT_SCROLL'; scrollTop: number }
  | { type: 'SET_TIMELINE_SCROLL'; scrollTop: number }
  | { type: 'INCREMENT_RESOLVED' }
  | { type: 'TOGGLE_AUTO_ADVANCE' }
  | { type: 'RESET_RESOLVED' }
  | { type: 'SET_SIDEBAR_OPEN'; value: boolean }
  | { type: 'TOGGLE_SIDEBAR' };

const initialState: WorkModeState = {
  focusMode: false,
  selectedModel: DEFAULT_MODEL_METADATA,
  selectedItemId: null,
  view: 'chat',
  chatScrollTop: 0,
  timelineScrollTop: 0,
  resolvedCount: 0,
  autoAdvance: false,
  sidebarOpen: false,
};

function workModeReducer(state: WorkModeState, action: WorkModeAction): WorkModeState {
  switch (action.type) {
    case 'TOGGLE_FOCUS_MODE':
      return { ...state, focusMode: !state.focusMode };
    case 'SET_FOCUS_MODE':
      return { ...state, focusMode: action.value };
    case 'SET_MODEL':
      return { ...state, selectedModel: action.model };
    case 'SET_SELECTED_ITEM':
      return { ...state, selectedItemId: action.itemId };
    case 'SET_VIEW':
      return { ...state, view: action.view };
    case 'SET_CHAT_SCROLL':
      return { ...state, chatScrollTop: action.scrollTop };
    case 'SET_TIMELINE_SCROLL':
      return { ...state, timelineScrollTop: action.scrollTop };
    case 'INCREMENT_RESOLVED':
      return { ...state, resolvedCount: state.resolvedCount + 1 };
    case 'TOGGLE_AUTO_ADVANCE':
      return { ...state, autoAdvance: !state.autoAdvance };
    case 'RESET_RESOLVED':
      return { ...state, resolvedCount: 0 };
    case 'SET_SIDEBAR_OPEN':
      return { ...state, sidebarOpen: action.value };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    default:
      return state;
  }
}

interface WorkModeContextType {
  state: WorkModeState;
  dispatch: React.Dispatch<WorkModeAction>;
}

const WorkModeContext = createContext<WorkModeContextType | undefined>(undefined);

export function WorkModeProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(workModeReducer, initialState);
  return (
    <WorkModeContext.Provider value={{ state, dispatch }}>
      {children}
    </WorkModeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkMode() {
  const context = useContext(WorkModeContext);
  if (!context) {
    throw new Error('useWorkMode must be used within WorkModeProvider');
  }
  return context;
}
